import { ChevronDown, ChevronRight, FileText, Plus, Search, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";

import type { Scene } from "../data/story";
import { IconButton } from "./IconButton";
import { useStoryPreferences } from "../i18n";

type OutlinePanelProps = {
  scenes: Scene[];
  selectedId: string;
  onSelect: (id: string) => void;
  onCreate?: () => void;
  creating?: boolean;
  totalWords?: number;
  overlay?: boolean;
};

export function OutlinePanel({ scenes, selectedId, onSelect, onCreate, creating = false, totalWords = 0, overlay = false }: OutlinePanelProps) {
  const { t } = useStoryPreferences();
  const [scrollTop, setScrollTop] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  function updateQuery(value: string) {
    setQuery(value);
    setScrollTop(0);
    if (listRef.current) listRef.current.scrollTop = 0;
  }
  const filteredScenes = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return normalized ? scenes.filter((scene) => `${scene.label} ${scene.title}`.toLocaleLowerCase().includes(normalized)) : scenes;
  }, [query, scenes]);
  const virtualized = filteredScenes.length > 80;
  const rowHeight = 42;
  const visibleScenes = useMemo(() => {
    if (!virtualized) return filteredScenes.map((scene, index) => ({ scene, index }));
    const start = Math.max(0, Math.floor(scrollTop / rowHeight) - 8);
    const end = Math.min(filteredScenes.length, start + 32);
    return filteredScenes.slice(start, end).map((scene, offset) => ({ scene, index: start + offset }));
  }, [filteredScenes, scrollTop, virtualized]);
  return (
    <aside className={`outline-panel${overlay ? " mobile-overlay-panel" : ""}`} aria-label={t("outlineLabel")}>
      {onCreate ? <button type="button" className="new-scene" disabled={creating} onClick={onCreate}><Plus /><span>{creating ? t("creatingScene") : t("newScene")}</span></button> : null}
      <div className="panel-heading works-heading">
        <h2>{t("outline")}</h2>
        <div><IconButton icon={searchOpen ? X : Search} label={searchOpen ? t("closeSearch") : t("outlineSearch")} onClick={() => { setSearchOpen((open) => !open); updateQuery(""); }} /></div>
      </div>
      {searchOpen ? <label className="outline-search"><span className="sr-only">{t("outlineSearch")}</span><Search aria-hidden="true" /><input autoFocus value={query} onChange={(event) => updateQuery(event.target.value)} placeholder={t("outlineSearch")} /></label> : null}
      <div ref={listRef} className="outline-list" role="listbox" aria-label={t("sceneList")} onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}>
        <div className={virtualized ? "outline-virtual-space" : undefined} style={virtualized ? { height: filteredScenes.length * rowHeight, position: "relative" } : undefined}>
        {visibleScenes.map(({ scene, index }) => (
          <button
            type="button"
            role="option"
            aria-selected={scene.id === selectedId}
            className={`outline-row${scene.id === selectedId ? " selected" : ""}${scene.nested ? " nested" : ""}`}
            key={scene.id}
            style={virtualized ? { position: "absolute", top: index * rowHeight, left: 0, right: 0, height: rowHeight } : undefined}
            onClick={() => onSelect(scene.id)}
          >
            <FileText size={18} strokeWidth={1.35} />
            <span>{scene.label}</span>
            {scene.expanded ? <ChevronDown size={17} /> : scene.id === "prologue" ? <ChevronRight size={17} /> : <span className="scene-state" />}
          </button>
        ))}
        {visibleScenes.length === 0 ? <p className="outline-empty">{t("noScenesFound")}</p> : null}
        </div>
      </div>
      <section className="writing-stats" aria-label={t("writingStats")}>
        <header><strong>{t("manuscriptStats")}</strong><span>{scenes.length} {t("scenes")}</span></header>
        <p><b>{totalWords.toLocaleString()}</b> {t("words")}</p><small>{t("liveProjectCount")}</small>
      </section>
    </aside>
  );
}

